import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Sparkles } from 'lucide-react';
import { isSpookySoundEnabled, setSpookySoundEnabled, playSpookySound } from './spookyAudio';

const HAUNTED_CURSES = [
    '⚡ THE HAUNTING INTENSIFIES! You disturbed the ancient crypt...',
    '🎃 THE SHADOWS AWAKEN! Keep an eye on the dark corners...',
    '👻 BOO! A spectral swarm has escaped into the storefront!',
    '🍬 TRICK OR TREAT! +13% Spooky Luck bestowed upon your cart!',
    '🦇 THE WITCHING HOUR HAS STRUCK! Beware what lurks below...',
    '💀 ANCIENT CURSE UNLOCKED! Spirits are watching you browse...'
];

const HauntedPumpkin = ({ onTriggerScare }) => {
    const [soundOn, setSoundOn] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [curseMessage, setCurseMessage] = useState('');
    const [pumpkinShock, setPumpkinShock] = useState(false);
    const curseTimeoutRef = useRef(null);

    useEffect(() => {
        setSoundOn(isSpookySoundEnabled());
    }, []);

    const toggleSound = (e) => {
        e.stopPropagation();
        const nextState = !soundOn;
        setSoundOn(nextState);
        setSpookySoundEnabled(nextState);
        if (nextState) {
            playSpookySound('chime');
        }
    };

    const handlePumpkinClick = () => {
        setPumpkinShock(true);
        setTimeout(() => setPumpkinShock(false), 600);

        // Sound FX
        playSpookySound('thunder');
        setTimeout(() => playSpookySound('cackle'), 250);

        // Random curse message
        const nextCurse = HAUNTED_CURSES[Math.floor(Math.random() * HAUNTED_CURSES.length)];
        setCurseMessage(nextCurse);
        if (curseTimeoutRef.current) clearTimeout(curseTimeoutRef.current);
        curseTimeoutRef.current = setTimeout(() => setCurseMessage(''), 2600);

        // Trigger global scare (screen lightning flash + bat burst)
        if (onTriggerScare) {
            onTriggerScare();
        }
    };

    return (
        <>
            {/* Global Spooky Curse Banner Popup - Strictly Centered & Safe on Mobile */}
            {curseMessage && (
                <div className="fixed top-14 md:top-20 inset-x-0 flex justify-center z-[76] pointer-events-none px-4">
                    <div className="w-full max-w-sm sm:max-w-md animate-bounce">
                        <div className="bg-gradient-to-r from-orange-600 via-purple-700 to-orange-700 text-white font-bold text-center px-4 py-2.5 rounded-2xl shadow-2xl shadow-purple-950/80 border border-orange-400/60 backdrop-blur-md flex items-center justify-center gap-2">
                            <Sparkles className="w-4 h-4 text-yellow-300 animate-spin shrink-0" />
                            <span className="text-xs sm:text-sm tracking-wide leading-snug">{curseMessage}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Pumpkin Mascot in Bottom Corner */}
            <div className="fixed bottom-4 sm:bottom-6 left-3 sm:left-6 z-[68] select-none flex flex-col items-start gap-1.5">
                {/* Speech Tooltip - Clamped within screen width */}
                {hovered && !curseMessage && (
                    <div className="bg-gray-950/95 text-orange-400 text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-xl border border-orange-500/50 shadow-lg shadow-black/60 animate-bounce max-w-[170px] sm:max-w-xs break-words mb-0.5">
                        Click me if you dare... 🎃
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-950 border-b border-r border-orange-500/50 rotate-45" />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* The Interactive Bat-Firing Pumpkin */}
                    <button
                        type="button"
                        onClick={handlePumpkinClick}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={`group relative outline-none focus:outline-none transition-all duration-300 ${
                            pumpkinShock ? 'animate-spooky-shake scale-125' : 'animate-float hover:scale-110 active:scale-95'
                        }`}
                        title="Click to summon the Halloween spirits!"
                    >
                        {/* Sinister Candlelight Glow Aura */}
                        <div className="absolute inset-0 rounded-full bg-orange-500/30 blur-xl group-hover:bg-orange-500/60 transition-all duration-500 pointer-events-none animate-candle-flicker" />

                        <svg viewBox="0 0 100 100" className="w-12 h-12 sm:w-14 sm:h-14 relative drop-shadow-[0_4px_16px_rgba(234,88,12,0.8)] touch-manipulation">
                            <defs>
                                <radialGradient id="pumpkinGlow" cx="50%" cy="55%" r="45%">
                                    <stop offset="0%" stopColor="#fef08a" stopOpacity="0.95" />
                                    <stop offset="60%" stopColor="#f97316" stopOpacity="0.45" />
                                    <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
                                </radialGradient>
                                <linearGradient id="pumpkinStem" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#15803d" />
                                    <stop offset="100%" stopColor="#14532d" />
                                </linearGradient>
                                <linearGradient id="pumpkinSkin" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#fb923c" />
                                    <stop offset="50%" stopColor="#ea580c" />
                                    <stop offset="100%" stopColor="#9a3412" />
                                </linearGradient>
                            </defs>

                            {/* Candle Radiance Glow */}
                            <circle cx="50" cy="55" r="30" fill="url(#pumpkinGlow)" className="animate-pulse" />

                            {/* Twisted Gnarled Stem */}
                            <path
                                d="M 48 24 C 47 12 56 6 62 4 C 59 10 56 16 53 24 Z"
                                fill="url(#pumpkinStem)"
                                stroke="#052e16"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />

                            {/* Outer Rib Segments (Back to Front) */}
                            <ellipse cx="23" cy="56" rx="18" ry="26" fill="#9a3412" />
                            <ellipse cx="77" cy="56" rx="18" ry="26" fill="#9a3412" />
                            <ellipse cx="34" cy="56" rx="18" ry="30" fill="#c2410c" />
                            <ellipse cx="66" cy="56" rx="18" ry="30" fill="#c2410c" />
                            <ellipse cx="50" cy="56" rx="19" ry="32" fill="url(#pumpkinSkin)" stroke="#7c2d12" strokeWidth="1" />

                            {/* Carved Sinister Eyes with Glow */}
                            <polygon points="32,44 42,44 37,33" fill="#fef08a" stroke="#b45309" strokeWidth="1" className="animate-pulse" />
                            <polygon points="58,44 68,44 63,33" fill="#fef08a" stroke="#b45309" strokeWidth="1" className="animate-pulse" />

                            {/* Carved Nose */}
                            <polygon points="46,51 54,51 50,45" fill="#fde047" stroke="#b45309" strokeWidth="0.8" />

                            {/* Wicked Jagged Smile */}
                            <path
                                d="M 26 62 Q 50 84 74 62 L 68 67 L 64 64 L 59 69 L 55 64 L 50 69 L 45 64 L 41 69 L 36 64 L 32 67 Z"
                                fill="#fef08a"
                                stroke="#b45309"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                                className="animate-candle-flicker"
                            />
                        </svg>
                    </button>

                    {/* Sound Toggle Pill */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        className="px-2.5 py-1.5 rounded-full bg-gray-900/90 hover:bg-gray-800 text-orange-400 border border-orange-500/40 text-[11px] font-bold flex items-center gap-1.5 shadow-md shadow-black/40 transition-all hover:scale-105 active:scale-95"
                        title={soundOn ? 'Spooky Audio Enabled (Click to Mute)' : 'Enable Spooky Ambience Audio'}
                    >
                        {soundOn ? (
                            <>
                                <Volume2 className="w-3.5 h-3.5 text-orange-400 animate-pulse" />
                                <span className="hidden sm:inline">Audio ON</span>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-3.5 h-3.5 text-gray-400" />
                                <span className="hidden sm:inline text-gray-400">Audio OFF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default HauntedPumpkin;
